// FNP-1248 — the read half.
//
// ⚠️ `secretRef.secretArn` IS DELIBERATELY NOT SELECTED ANYWHERE IN THIS FILE.
//
// The schema exposes it and explains why it is safe to (it is an address, not a
// secret, and reading the secret needs IAM no API caller holds). That makes it
// safe for an OPERATOR tool. It is still infrastructure detail with no tenant
// audience, and this module renders a tenant-facing console — so not asking for
// it is one fewer place it can end up in a log, a cache entry, a screenshot or
// a support ticket. The zones module made the same call for the same reason.
//
// `externalId` IS selected, on every query that returns a connection, because
// it is the one value Flexi must TELL the tenant. See types.ts.

const CAPABILITY_FIELDS = `
    supportedRecordTypes
    canWriteRecords
    canCreateZones
    canDeleteZones
    canSetNameservers
`;

// ⚠️ `externalId` ONLY — see the file header. Adding `secretArn` here is the
// one edit that would put an infrastructure ARN in front of a tenant.
const SECRET_REF_FIELDS = `
    externalId
`;

// FNP-1377 — THE TENANT'S DECLARATIONS, distinct from the observed
// `capabilities` above and previously selected by neither this file nor
// `mutations.ts`. See the fuller note there; in short, `isWritableDeclared`
// read `undefined` on every connection, so the detail page said "Declared to
// write: READ-ONLY" regardless of what the row stored.
//
// ⚠️ KEPT IN STEP WITH `mutations.ts`. Both files build a selection set for the
// same type, and a field present in one and missing from the other means the
// UI is right after a connect and wrong after a reload, or the reverse — the
// hardest shape of this bug to see. `tests/selectionSet.test.ts` asserts they
// agree.
const DECLARATION_FIELDS = `
    dnsCapabilities {
        canWriteRecords
        canCreateZones
        canDeleteZones
    }
    certificateCapabilities {
        canRequest
        canDelete
        supportedRegions
    }
`;

const PROVIDER_CONNECTION_FIELDS = `
    id
    providerId
    displayName
    credentialType
    status
    statusDetail
    lastValidatedAt
    capabilities {${CAPABILITY_FIELDS}}
    ${DECLARATION_FIELDS}
    secretRef {${SECRET_REF_FIELDS}}
    createdByUsername
    created
    updated
`;

// ⚠️ Lists ONLY the calling tenant's connections — the tenant is a KEY
// CONDITION on the server query, not a filter applied afterwards, so there is
// no accountId to pass and none is accepted.
export const listProviderConnections = `
   query listProviderConnections($filter: ProviderConnectionsFilterInput, $limit: Int, $nextToken: String) {
       listProviderConnections(filter: $filter, limit: $limit, nextToken: $nextToken) {
           items {${PROVIDER_CONNECTION_FIELDS}}
           nextToken
       }
   }
`;

export const getProviderConnection = `
   query getProviderConnection($id: ID!) {
       getProviderConnection(id: $id) {${PROVIDER_CONNECTION_FIELDS}}
   }
`;

// THE CATALOG (FNP-1179). Drives the connect form's provider picker, and
// `credentialType` is what the credential field switches on — so it is not
// optional to select it: without it the form cannot know which field to render.
export const listDnsProviders = `
   query listDnsProviders($filter: DnsProvidersFilterInput, $limit: Int, $nextToken: String) {
       listDnsProviders(filter: $filter, limit: $limit, nextToken: $nextToken) {
           items {
               id
               name
               credentialType
               capabilities {${CAPABILITY_FIELDS}}
           }
           nextToken
       }
   }
`;

// ⚠️ WHAT DISCONNECTING WOULD COST, READ BEFORE IT COSTS IT.
//
// A Query because it reads and mutates nothing — separate from the mutation
// rather than a `dryRun` flag on it, because a preview sharing a code path with
// the deletion is one inverted boolean away from BEING the deletion.
//
// ⚠️ `zoneCountUnavailable` IS SELECTED ALONGSIDE `managedZoneCount` AND BOTH
// ARE MANDATORY. The count alone is unreadable: null means UNKNOWN, and a
// client that selected only the number could not tell an unknown from a zero.
// Every consumer goes through `zoneImpact()`, which reads the flag first.
export const previewProviderDisconnect = `
   query previewProviderDisconnect($id: ID!) {
       previewProviderDisconnect(id: $id) {
           connectionId
           displayName
           providerId
           status
           managedZoneCount
           zoneCountUnavailable
           secretName
           consequences
       }
   }
`;

// ⚠️ NOT A FIELD ON `ProviderConnection`, DELIBERATELY. The customer needs this
// BEFORE a connection exists — it goes into the trust policy of the role they
// must create in order to connect at all. Hanging it off the connection would
// publish it only to people who no longer need it.
export const getFlexiPrincipal = `
   query flexiPrincipal {
       flexiPrincipal {
           arn
           scope
       }
   }
`;
